import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Check, ChartBar, Shield, Lock, HexagonIcon, ExternalLink, ChevronDown, ChevronLeft, ChevronRight, Github, Twitter, MessageCircle, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
// import OHLCVChart from 'components/OHLCV';
import { 
  getPortfolioAssetSearch, 
  getTokenBankData, 
  getSecurityScores, 
  getTokenMetadata, 
  // getTokenOHLCV, 
  // getSingleTokenHistoricalHolder, 
  getTokenPairData } from 'services/portfolio';
import styles from './Discovery.module.sass';
import CompactTokenPairMetrics from 'components/CompactTokenPairMetrics';
import Spinner from 'components/Spinner';
// import DiversificationAnimation from 'components/DiversificationAnimation';
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
// import TokenSniper from 'components/TokenSniper';
import HolderAnalyticsDetailed from 'components/HolderAnalyticsDetailed';
import PortfolioImpactComponent from 'components/PortfolioImpact';
import RugCheckComponent from 'components/RugCheck';
import LiveMonitor from 'components/LiveMonitor';
import { Command, Search, TrendingUp, SlidersHorizontal, Sparkles } from 'lucide-react';
import AssetRationaleModal from 'components/Modal/AssetRationaleModal';
import SearchGuide from 'components/Modal/TokenSearchGuidelines';
import { ProPlusBadgeSmall, ProBadge, ProSection, ProBadgeSmall } from 'utils/ProBadge';
import { ConstructionBadge, ConstructionBadgeSmall, ConstructionSection } from 'utils/ConstructionBadge';
import { getDetailedHistoricHolders } from 'services/portfolio';
import allAssetsImage from '../../../assets/AT.png';


const ITEMS_PER_PAGE = 5;

const getRiskLevel = (score) => {
  if (score === "NOT_FOUND") return "Not Available";
  if (score < 2) return "High Risk";
  if (score < 3.5) return "Moderate Risk";
  return "Low Risk";
};


const renderMetric = (label, value, score, tooltipText) => {
  // If score is "NOT_FOUND", it indicates data not found
  if (score === "NOT_FOUND") {
    return (
      <div className={styles.metricBox} title={tooltipText}>
        <div className={styles.metricLabel}>{label}</div>
        <div className={styles.securityScoreCell}>
          <div className={styles.securityScoreContent} style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            color: 'rgba(115, 246, 255, 0.5)',
            fontSize: '0.9em',
            height: '100%'
          }}>
            Data not available
          </div>
        </div>
      </div>
    );
  }

  const getScoreColor = (value) => {
    if (value < 2) return '#f44336';
    if (value < 3.5) return '#9c27b0';
    return '#73F6FF';
  };

  const colorStyles = {
    progress: {
      background: getScoreColor(score),
      boxShadow: `0 0 10px ${getScoreColor(score)}40`
    }
  };

  const normalizedScore = score / 5;

  return (
    <div className={styles.metricBox} title={tooltipText}>
      <div className={styles.metricLabel}>{label}</div>
      <div className={styles.securityScoreCell}>
        <div className={styles.securityScoreContent}>
          <div className={styles.scoreSegments}>
            {[0,1,2,3,4].map((segment) => {
              const isFullyFilled = segment < Math.floor(normalizedScore * 5);
              const isPartiallyFilled = segment === Math.floor(normalizedScore * 5);
              const partialFillPercentage = isPartiallyFilled ? ((normalizedScore * 5) % 1) * 100 : 0;
              
              let segmentStyle = {};
              if (isFullyFilled) {
                segmentStyle = colorStyles.progress;
              } else if (isPartiallyFilled && partialFillPercentage > 0) {
                const color = getScoreColor(score);
                segmentStyle = {
                  background: `linear-gradient(to right, ${color} ${partialFillPercentage}%, rgba(115, 246, 255, 0.1) ${partialFillPercentage}%)`,
                  boxShadow: colorStyles.progress.boxShadow
                };
              }
              
              return (
                <div 
                  key={segment}
                  className={`${styles.segment} ${isFullyFilled ? styles.segmentFilled : ''}`}
                  style={segmentStyle}
                />
              );
            })}
          </div>
          <div className={styles.metricDetails}>
            <span 
              className={styles.securityScoreValue}
              style={{ color: getScoreColor(score) }}
            >
              {value}
            </span>
            <span className={styles.scoreValue}>
              {score.toFixed(1)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DataFetchingComponent = () => {
  const auth = useAuth();

  const stripeConfirmation = useSelector((state: RootState) => state.user.stripeConfirmation);
  const [query, setQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [tokenBankData, setTokenBankData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);
  const [securityScores, setSecurityScores] = useState({});
  const [tokenMetadata, setTokenMetadata] = useState(null);
  const [holderCountHistory, setHolderCountHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [pairData, setPairData] = useState([]);
  const wallets = useSelector((state: RootState) => state.user.wallets);

  // scores
  const [holderScore, setHolderScore] = useState(null);
  const [buySellScore, setBuySellScore] = useState(null);
  const [liquidityScore, setLiquidityScore] = useState(null);
  const [marketActivityScore, setMarketActivityScore] = useState(null);

  // table filters
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStrategy, setActiveStrategy] = useState<string | null>(null);
  const [activeStrategyBanner, setActiveStrategyBanner] = useState(null);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [filteredTopAssets, setFilteredTopAssets] = useState([]);
  const [topAssetsMetadata, setTopAssetsMetadata] = useState({});

  const [rationaleModalOpen, setRationaleModalOpen] = useState(false);
  const [selectedRationaleToken, setSelectedRationaleToken] = useState(null);
  const [isSearchGuideOpen, setIsSearchGuideOpen] = useState(false);
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  
  const removeFilter = (filterToRemove) => {
    setActiveFilters(activeFilters.filter(f => f !== filterToRemove));
  };

  const parseFilterExpression = (query) => {
    const normalizedQuery = query.toLowerCase().trim();
    
    // Modified special queries detection using more flexible patterns
    const topPerformersPattern = /\btop\b.*\bperformers?\b/i;
    const bottomPerformersPattern = /\bbottom\b.*\bperformers?\b/i;
    const topReturnsPattern = /\btop\b.*\breturns?\b/i;
    const bottomReturnsPattern = /\bbottom\b.*\breturns?\b/i;
    const highestCapPattern = /\bhighest\b.*\b(market cap|cap)\b/i;
    const lowestCapPattern = /\blowest\b.*\b(market cap|cap)\b/i;
    const bestPerformerPattern = /\bbest\b.*\bperformer\b/i;
    const worstPerformerPattern = /\bworst\b.*\bperformer\b/i;
    
    // Extract number limit if present
    const numMatch = normalizedQuery.match(/\b(top|bottom)\s+(\d+)\b/i);
    const limit = numMatch ? parseInt(numMatch[2]) : 10;
    
    // Check for time period modifiers
    const isWeekly = normalizedQuery.includes('week') || normalizedQuery.includes('7d');
    const field = isWeekly ? '7d' : '24h';
    
    // Check for each pattern type
    if (topPerformersPattern.test(normalizedQuery) || topReturnsPattern.test(normalizedQuery) || bestPerformerPattern.test(normalizedQuery)) {
      return {
        type: 'special',
        query: 'top_performers',
        field: field,
        limit: bestPerformerPattern.test(normalizedQuery) ? 1 : limit
      };
    }
    
    if (bottomPerformersPattern.test(normalizedQuery) || bottomReturnsPattern.test(normalizedQuery) || worstPerformerPattern.test(normalizedQuery)) {
      return {
        type: 'special',
        query: 'bottom_performers',
        field: field,
        limit: worstPerformerPattern.test(normalizedQuery) ? 1 : limit
      };
    }
    
    if (highestCapPattern.test(normalizedQuery)) {
      return {
        type: 'special',
        query: 'top_performers',
        field: 'cap',
        limit: limit
      };
    }
    
    if (lowestCapPattern.test(normalizedQuery)) {
      return {
        type: 'special',
        query: 'bottom_performers',
        field: 'cap',
        limit: limit
      };
    }
    
    // Enhanced natural language mappings
    const naturalLanguageMap = {
      'over': '>',
      'under': '<',
      'above': '>',
      'below': '<',
      'more than': '>',
      'less than': '<',
      'at least': '>=',
      'greater than': '>',
      'higher than': '>',
      'lower than': '<',
      'equal to': '=',
      'equals': '=',
      'exactly': '=',
      'around': '~',
      'approximately': '~',
      'about': '~',
      'near': '~',
      'between': 'range'
    };
  
    // Expanded field aliases with common variations + chain support
    const fieldMap = {
      'returns': '24h',
      'return': '24h',
      'price': 'price',
      'worth': 'price',
      'costs': 'price',
      'value': 'price',
      'cap': 'cap',
      'marketcap': 'cap',
      'market cap': 'cap',
      'market': 'cap',
      'capitalization': 'cap',
      '24h': '24h',
      '24hr': '24h',
      '24 hour': '24h',
      '24 hours': '24h',
      'daily': '24h',
      'day': '24h',
      '7d': '7d',
      '7 day': '7d',
      '7 days': '7d',
      'weekly': '7d',
      'week': '7d',
      // Chain mappings
      'chain': 'chain',
      'network': 'chain',
      'blockchain': 'chain',
      'on': 'chain'
    };

    // Chain value mappings for common aliases
    const chainValueMap = {
      'ethereum': 'eth',
      'eth': 'eth',
      'bitcoin': 'btc',
      'btc': 'btc',
      'binance': 'bsc',
      'bsc': 'bsc',
      'bnb': 'bsc',
      'polygon': 'polygon',
      'matic': 'polygon',
      'avalanche': 'avax',
      'avax': 'avax',
      'solana': 'sol',
      'sol': 'sol',
      'cardano': 'ada',
      'ada': 'ada',
      'arbitrum': 'arbitrum',
      'optimism': 'optimism',
      'base': 'base'
    };
  
    // Handle "between" ranges
    const rangePattern = /(\w+)\s*between\s*(-?\d+\.?\d*)\s*and\s*(-?\d+\.?\d*)/i;
    const rangeMatch = query.match(rangePattern);
    if (rangeMatch) {
      const [, field, min, max] = rangeMatch;
      return {
        type: 'range',
        field: fieldMap[field] || field,
        min: parseFloat(min),
        max: parseFloat(max)
      };
    }
  
    // Handle approximate values
    const approximatePattern = /(\w+)\s*(around|about|approximately|near)\s*(-?\d+\.?\d*)/i;
    const approximateMatch = query.match(approximatePattern);
    if (approximateMatch) {
      const [, field, , value] = approximateMatch;
      const numValue = parseFloat(value);
      return {
        type: 'approximate',
        field: fieldMap[field] || field,
        value: numValue,
        tolerance: numValue * 0.1 // 10% tolerance by default
      };
    }

    // Handle chain-specific text matching (e.g., "chain = eth", "on ethereum")
    const chainTextPattern = /(chain|network|blockchain|on)\s*(=|is|equals?)\s*(\w+)/i;
    const chainTextMatch = query.match(chainTextPattern);
    if (chainTextMatch) {
      const [, , , chainValue] = chainTextMatch;
      return {
        type: 'text',
        field: 'chain',
        value: chainValueMap[chainValue.toLowerCase()] || chainValue.toLowerCase(),
        matchType: 'exact'
      };
    }

    // Handle simple chain mentions (e.g., "ethereum", "base", "polygon")
    // Check for exact word matches to avoid false positives
    for (const [alias, value] of Object.entries(chainValueMap)) {
      const wordPattern = new RegExp(`\\b${alias}\\b`, 'i');
      if (wordPattern.test(normalizedQuery)) {
        return {
          type: 'text',
          field: 'chain',
          value: value,
          matchType: 'exact'
        };
      }
    }
  
    // Natural language pattern matching with expanded variations
    for (const [phrase, operator] of Object.entries(naturalLanguageMap)) {
      for (const [fieldPhrase, fieldName] of Object.entries(fieldMap)) {
        // Handle percentage values
        const percentPattern = new RegExp(
          `${fieldPhrase}\\s*${phrase}\\s*(-?\\d+\.?\\d*)\\s*%?`, 'i'
        );
        const match = query.match(percentPattern);
        if (match) {
          return {
            type: 'numeric',
            field: fieldName,
            operator: operator,
            value: parseFloat(match[1]),
            isPercentage: query.includes('%')
          };
        }

        // Handle text values for chain field
        if (fieldName === 'chain') {
          const textPattern = new RegExp(
            `${fieldPhrase}\\s*${phrase}\\s*(\\w+)`, 'i'
          );
          const textMatch = query.match(textPattern);
          if (textMatch) {
            const chainValue = textMatch[1];
            return {
              type: 'text',
              field: 'chain',
              value: chainValueMap[chainValue.toLowerCase()] || chainValue.toLowerCase(),
              matchType: operator === '=' ? 'exact' : 'contains'
            };
          }
        }
      }
    }
  
    // Enhanced numeric comparison with unit handling
    const numericRegex = /(\w+)\s*(>|<|>=|<=|=)\s*(-?\d+\.?\d*)\s*(k|m|b|t|%)?/i;
    const match = query.toLowerCase().match(numericRegex);
    
    if (match) {
      const [, field, operator, value, unit] = match;
      let numValue = parseFloat(value);
      
      // Handle units (k = thousands, m = millions, b = billions, t = trillions)
      if (unit) {
        switch(unit.toLowerCase()) {
          case 'k': numValue *= 1000; break;
          case 'm': numValue *= 1000000; break;
          case 'b': numValue *= 1000000000; break;
          case 't': numValue *= 1000000000000; break;
        }
      }
      
      return {
        type: 'numeric',
        field: fieldMap[field] || field,
        operator,
        value: numValue,
        isPercentage: unit === '%'
      };
    }
  
    if (normalizedQuery.startsWith('not ')) {
      return {
        type: 'text',
        value: normalizedQuery.replace('not ', ''),
        matchType: 'exclusion'
      };
    }
    if (normalizedQuery.startsWith('"') && normalizedQuery.endsWith('"')) {
      return {
        type: 'text',
        value: normalizedQuery.slice(1, -1),
        matchType: 'exact'
      };
    }
    
    // Default text search
    return {
      type: 'text',
      value: normalizedQuery,
      matchType: 'contains'
    };
  };
  // const parseFilterExpression = (query) => {
  //   const normalizedQuery = query.toLowerCase().trim();
  //   
  //   // Modified special queries detection using more flexible patterns
  //   const topPerformersPattern = /\btop\b.*\bperformers?\b/i;
  //   const bottomPerformersPattern = /\bbottom\b.*\bperformers?\b/i;
  //   const topReturnsPattern = /\btop\b.*\breturns?\b/i;
  //   const bottomReturnsPattern = /\bbottom\b.*\breturns?\b/i;
  //   const highestCapPattern = /\bhighest\b.*\b(market cap|cap)\b/i;
  //   const lowestCapPattern = /\blowest\b.*\b(market cap|cap)\b/i;
  //   const bestPerformerPattern = /\bbest\b.*\bperformer\b/i;
  //   const worstPerformerPattern = /\bworst\b.*\bperformer\b/i;
  //   
  //   // Extract number limit if present
  //   const numMatch = normalizedQuery.match(/\b(top|bottom)\s+(\d+)\b/i);
  //   const limit = numMatch ? parseInt(numMatch[2]) : 10;
  //   
  //   // Check for time period modifiers
  //   const isWeekly = normalizedQuery.includes('week') || normalizedQuery.includes('7d');
  //   const field = isWeekly ? '7d' : '24h';
  //   
  //   // Check for each pattern type
  //   if (topPerformersPattern.test(normalizedQuery) || topReturnsPattern.test(normalizedQuery) || bestPerformerPattern.test(normalizedQuery)) {
  //     return {
  //       type: 'special',
  //       query: 'top_performers',
  //       field: field,
  //       limit: bestPerformerPattern.test(normalizedQuery) ? 1 : limit
  //     };
  //   }
  //   
  //   if (bottomPerformersPattern.test(normalizedQuery) || bottomReturnsPattern.test(normalizedQuery) || worstPerformerPattern.test(normalizedQuery)) {
  //     return {
  //       type: 'special',
  //       query: 'bottom_performers',
  //       field: field,
  //       limit: worstPerformerPattern.test(normalizedQuery) ? 1 : limit
  //     };
  //   }
  //   
  //   if (highestCapPattern.test(normalizedQuery)) {
  //     return {
  //       type: 'special',
  //       query: 'top_performers',
  //       field: 'cap',
  //       limit: limit
  //     };
  //   }
  //   
  //   if (lowestCapPattern.test(normalizedQuery)) {
  //     return {
  //       type: 'special',
  //       query: 'bottom_performers',
  //       field: 'cap',
  //       limit: limit
  //     };
  //   }
  //   
  //   // Enhanced natural language mappings
  //   const naturalLanguageMap = {
  //     'over': '>',
  //     'under': '<',
  //     'above': '>',
  //     'below': '<',
  //     'more than': '>',
  //     'less than': '<',
  //     'at least': '>=',
  //     'greater than': '>',
  //     'higher than': '>',
  //     'lower than': '<',
  //     'equal to': '=',
  //     'equals': '=',
  //     'exactly': '=',
  //     'around': '~',
  //     'approximately': '~',
  //     'about': '~',
  //     'near': '~',
  //     'between': 'range'
  //   };
  // 
  //   // Expanded field aliases with common variations
  //   const fieldMap = {
  //     'returns': '24h',
  //     'return': '24h',
  //     'price': 'price',
  //     'worth': 'price',
  //     'costs': 'price',
  //     'value': 'price',
  //     'cap': 'cap',
  //     'marketcap': 'cap',
  //     'market cap': 'cap',
  //     'market': 'cap',
  //     'capitalization': 'cap',
  //     '24h': '24h',
  //     '24hr': '24h',
  //     '24 hour': '24h',
  //     '24 hours': '24h',
  //     'daily': '24h',
  //     'day': '24h',
  //     '7d': '7d',
  //     '7 day': '7d',
  //     '7 days': '7d',
  //     'weekly': '7d',
  //     'week': '7d'
  //   };
  // 
  //   // Handle "between" ranges
  //   const rangePattern = /(\w+)\s*between\s*(-?\d+\.?\d*)\s*and\s*(-?\d+\.?\d*)/i;
  //   const rangeMatch = query.match(rangePattern);
  //   if (rangeMatch) {
  //     const [, field, min, max] = rangeMatch;
  //     return {
  //       type: 'range',
  //       field: fieldMap[field] || field,
  //       min: parseFloat(min),
  //       max: parseFloat(max)
  //     };
  //   }
  // 
  //   // Handle approximate values
  //   const approximatePattern = /(\w+)\s*(around|about|approximately|near)\s*(-?\d+\.?\d*)/i;
  //   const approximateMatch = query.match(approximatePattern);
  //   if (approximateMatch) {
  //     const [, field, , value] = approximateMatch;
  //     const numValue = parseFloat(value);
  //     return {
  //       type: 'approximate',
  //       field: fieldMap[field] || field,
  //       value: numValue,
  //       tolerance: numValue * 0.1 // 10% tolerance by default
  //     };
  //   }
  // 
  //   // Natural language pattern matching with expanded variations
  //   for (const [phrase, operator] of Object.entries(naturalLanguageMap)) {
  //     for (const [fieldPhrase, fieldName] of Object.entries(fieldMap)) {
  //       // Handle percentage values
  //       const percentPattern = new RegExp(
  //         `${fieldPhrase}\\s*${phrase}\\s*(-?\\d+\.?\\d*)\\s*%?`, 'i'
  //       );
  //       const match = query.match(percentPattern);
  //       if (match) {
  //         return {
  //           type: 'numeric',
  //           field: fieldName,
  //           operator: operator,
  //           value: parseFloat(match[1]),
  //           isPercentage: query.includes('%')
  //         };
  //       }
  //     }
  //   }
  // 
  //   // Enhanced numeric comparison with unit handling
  //   const numericRegex = /(\w+)\s*(>|<|>=|<=|=)\s*(-?\d+\.?\d*)\s*(k|m|b|t|%)?/i;
  //   const match = query.toLowerCase().match(numericRegex);
  //   
  //   if (match) {
  //     const [, field, operator, value, unit] = match;
  //     let numValue = parseFloat(value);
  //     
  //     // Handle units (k = thousands, m = millions, b = billions, t = trillions)
  //     if (unit) {
  //       switch(unit.toLowerCase()) {
  //         case 'k': numValue *= 1000; break;
  //         case 'm': numValue *= 1000000; break;
  //         case 'b': numValue *= 1000000000; break;
  //         case 't': numValue *= 1000000000000; break;
  //       }
  //     }
  //     
  //     return {
  //       type: 'numeric',
  //       field: fieldMap[field] || field,
  //       operator,
  //       value: numValue,
  //       isPercentage: unit === '%'
  //     };
  //   }
  // 
  //   if (normalizedQuery.startsWith('not ')) {
  //     return {
  //       type: 'text',
  //       value: normalizedQuery.replace('not ', ''),
  //       matchType: 'exclusion'
  //     };
  //   }
  //   if (normalizedQuery.startsWith('"') && normalizedQuery.endsWith('"')) {
  //     return {
  //       type: 'text',
  //       value: normalizedQuery.slice(1, -1),
  //       matchType: 'exact'
  //     };
  //   }
  //   
  //   // Default text search
  //   return {
  //     type: 'text',
  //     value: normalizedQuery,
  //     matchType: 'contains'
  //   };
  // };
  
  // Add debugging to handleStrategyClick
  const handleStrategySelect = (strategy) => {
    setSelectedStrategy(strategy);
    setIsModalOpen(true);
    setIsFilterDropdownOpen(false);
  };
  

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const fetchHolderHistory = async (address, chain) => {
    try {
      if (!auth.user?.access_token) {
        toast.error('User not authenticated');
        return;
      }
  
      // Calculate fromDate (e.g., 30 days ago from today) in UTC
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      // Format as ISO string in UTC
      const fromDate = thirtyDaysAgo.toISOString();
      
      // Use the getDetailedHistoricHolders function
      const data = await getDetailedHistoricHolders(
        auth.user.access_token,
        address,
        fromDate,
        // "eth", // Default chain
        chain, // Dynamic chain
        "4h"   // Default interval
      );
      
      // Verify we have result data
      if (!data || !data.result || !Array.isArray(data.result)) {
        console.error('Invalid data format returned from API');
        setHolderCountHistory([]);
        setHolderScore(0);
        return;
      }
      
      // Format data using local time
      const formattedData = data.result.map(item => {
        // Create a date object from the timestamp
        const date = new Date(item.timestamp);
        
        // Format as local date (browser's timezone)
        const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
        
        return {
          date: formattedDate,
          holders: item.totalHolders,
          timestamp: item.timestamp // Keep original timestamp for tooltip
        };
      });
      
      // Sort the data chronologically
      const sortedData = formattedData.sort((a, b) => {
        // Create date objects just for comparison
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return dateA.getTime() - dateB.getTime();
      });
  
      setHolderCountHistory(sortedData);
  
      // Calculate holder score if we have enough data points
      if (sortedData.length >= 2) {
        const firstCount = sortedData[0].holders;
        const lastCount = sortedData[sortedData.length - 1].holders;
        
        // Calculate growth score (60% of total)
        const growthPercent = ((lastCount - firstCount) / firstCount) * 100;
        const normalizedGrowth = Math.min(Math.max(growthPercent, -100), 100) / 20; // Convert to 0-5 scale
        
        // Calculate retention score (40% of total)
        let drops = 0;
        let totalDropPercent = 0;
        for (let i = 1; i < sortedData.length; i++) {
          const change = sortedData[i].holders - sortedData[i-1].holders;
          if (change < 0) {
            drops++;
            totalDropPercent += (change / sortedData[i-1].holders) * 100;
          }
        }
        const retentionRate = drops === 0 ? 5 : Math.max(5 + (totalDropPercent / drops) / 20, 0);
        
        // Calculate final score (0-5 scale)
        const finalScore = Math.max(Math.min(
          (normalizedGrowth * 0.6) + (retentionRate * 0.4),
          5
        ), 0);
        
        setHolderScore(finalScore);
      } else {
        setHolderScore(0);
      }
    } catch (error) {
      console.error('Error fetching holder history');
      setHolderCountHistory([]);
      setHolderScore(0);
    }
  };

  // Custom tooltip component to display formatted time with timezone support
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Get the original timestamp from the data point
      const originalTimestamp = payload[0].payload.timestamp;
      
      // Format date in local timezone
      let formattedDate;
      if (originalTimestamp) {
        const date = new Date(originalTimestamp);
        formattedDate = date.toLocaleString(); // Local timezone
      } else {
        formattedDate = label; // Fallback to label if timestamp not available
      }
      
      return (
        <div className={styles.tooltip} style={{ 
          backgroundColor: 'rgba(23, 23, 30, 0.95)', 
          borderColor: 'rgba(115, 246, 255, 0.2)',
          color: '#73F6FF',
          padding: '10px',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          <p className={styles.tooltipTime} style={{ color: '#73F6FF', fontWeight: 600, marginBottom: '5px' }}>
            {formattedDate}
          </p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} style={{ color: entry.color, margin: '2px 0' }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const filterTokens = (tokens, query) => {
    if (!query) return tokens;
    const filter = parseFilterExpression(query);
    
    if (filter.type === 'special') {
      const field = filter.field === 'cap' ? 'market_cap_usd' : 
                    filter.field === '24h' ? 'price_24h_percent_change' : 
                    'price_7d_percent_change';
      
      const sortedTokens = [...tokens].sort((a, b) => {
        const aValue = Number(a[field]) || 0;
        const bValue = Number(b[field]) || 0;
        return filter.query === 'top_performers' ? 
          bValue - aValue : 
          aValue - bValue;
      });
      
      return sortedTokens.slice(0, filter.limit);
    }
    
    return tokens.filter(token => {
      if (filter.type === 'range') {
        const compareValue = getCompareValue(token, filter.field);
        return compareValue >= filter.min && compareValue <= filter.max;
      }
  
      if (filter.type === 'approximate') {
        const compareValue = getCompareValue(token, filter.field);
        const minValue = filter.value - filter.tolerance;
        const maxValue = filter.value + filter.tolerance;
        return compareValue >= minValue && compareValue <= maxValue;
      }
  
      if (filter.type === 'numeric') {
        const compareValue = getCompareValue(token, filter.field);
        if (compareValue === null) return false;
  
        switch (filter.operator) {
          case '>': return compareValue > filter.value;
          case '<': return compareValue < filter.value;
          case '>=': return compareValue >= filter.value;
          case '<=': return compareValue <= filter.value;
          case '=': return Math.abs(compareValue - filter.value) < 0.000001; // Handle floating point comparison
          case '~': return Math.abs(compareValue - filter.value) <= filter.value * 0.1;
          default: return true;
        }
      }


      // Handle chain filtering
      if (filter.field === 'chain') {
        const tokenChain = token.chain ? token.chain.toLowerCase() : '';
        switch (filter.matchType) {
          case 'exact':
            return tokenChain === filter.value;
          case 'exclusion':
            return tokenChain !== filter.value;
          default:
            return tokenChain.includes(filter.value);
        }
      }

      // Enhanced text search
      const searchText = `${token.token_symbol} ${token.token_name}`.toLowerCase();
      switch (filter.matchType) {
        case 'exclusion':
          return !searchText.includes(filter.value);
        case 'exact':
          return new RegExp(`\\b${filter.value}\\b`, 'i').test(searchText);
        case 'startsWith':
          return searchText.startsWith(filter.value);
        case 'endsWith':
          return searchText.endsWith(filter.value);
        default:
          return searchText.includes(filter.value);
      }
    });
  };

  const getCompareValue = (token, field) => {
    switch (field) {
      case 'price':
        return Number(token.price_usd);
      case '24h':
      case 'returns':
        return Number(token.price_24h_percent_change);
      case '7d':
        return Number(token.price_7d_percent_change);
      case 'cap':
      case 'marketcap':
        return Number(token.market_cap_usd);
      default:
        return null;
    }
  };

  const applyMultipleFilters = (tokens, filters) => {
    return filters.reduce((filtered, filter) => {
      return filterTokens(filtered, filter);
    }, tokens);
  };

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
  
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
  
      return () => clearTimeout(timer);
    }, [value, delay]);
  
    return debouncedValue;
  };

  const StrategyModal = ({ strategy, onClose }) => {
    const [assetCount, setAssetCount] = useState(5);
    const [isDiscovering, setIsDiscovering] = useState(false);
    const [searchPromise, setSearchPromise] = useState(null);
    const wallets = useSelector((state: RootState) => state.user.wallets);
  
    if (!strategy || !strategy.id || !strategy.label) {
      console.error('Invalid strategy object:', strategy);
      return null;
    }
        
    if (!strategy) return null;
  
    const getStrategyColor = (id) => {
      switch (id) {
        case 'diversify': return '#73F6FF';
        case 'juice': return '#47ff9c';
        case 'secure': return '#9c27b0';
        case 'risk': return '#ffd700';
        default: return '#73F6FF';
      }
    };
  
    const getMaxAssets = (strategyId) => {
      switch(strategyId) {
        case 'secure': return 10;
        case 'juice': return 15;
        case 'diversify': return 25;
        default: return 25;
      }
    };
  
    const assetCountOptions = [
      { value: 5, label: '5 assets' },
      { value: 8, label: '8 assets' },
      { value: 10, label: '10 assets' },
    ].filter(option => option.value <= getMaxAssets(strategy.id));
  
    const selectStyles = {
      wrapper: {
        position: 'relative',
        width: '100%'
      },
      select: {
        width: '100%',
        background: 'rgba(115, 246, 255, 0.1)',
        border: '1px solid rgba(115, 246, 255, 0.2)',
        borderRadius: '6px',
        color: getStrategyColor(strategy.id),
        padding: '8px 12px',
        fontSize: '1em',
        cursor: 'pointer',
        outline: 'none',
        minWidth: '140px',
        appearance: 'none',
        transition: 'all 0.2s ease',
        backdropFilter: 'blur(5px)',
        '&:hover': {
          background: 'rgba(115, 246, 255, 0.15)',
          borderColor: 'rgba(115, 246, 255, 0.3)'
        },
        '&:focus': {
          boxShadow: '0 0 0 2px rgba(115, 246, 255, 0.2)'
        }
      },
      arrow: {
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        opacity: 0.6,
        color: getStrategyColor(strategy.id)
      }
    };
  
    const handleStartDiscovery = async () => {
      setIsDiscovering(true);
      
      // Format the strategy
      const formattedStrategy = strategy.id.charAt(0).toUpperCase() + strategy.id.slice(1);
      
      try {
        // Start the API call and store the promise
        const promise = getPortfolioAssetSearch(
          auth.user?.access_token,
          wallets,
          'ETH', // default benchmark
          4,
          formattedStrategy,
          assetCount
        );
        
        // Add logging to the promise directly
        promise.catch(error => {
          console.error('API call failed');
        });
        
        // Store the promise so the loading sequence can track it
        setSearchPromise(promise);
      } catch (error) {
        console.error('Error starting discovery (caught)');
        setIsDiscovering(false);
        toast.error('Failed to start asset discovery');
      }
    };
  
    const handleDiscoveryComplete = async (result) => {
      try {
        if (!result) {
          toast.error('Search failed. Please try again.');
          setIsDiscovering(false);
          return;
        }
        
        const formattedStrategy = strategy.id.charAt(0).toUpperCase() + strategy.id.slice(1);
        const filteredData = tokenBankData.filter(token => 
          result.suggested_tokens.includes(token.token_symbol)
        );
        
        setTokenBankData(filteredData);
        setActiveStrategy(strategy.id);
        setActiveStrategyBanner({
          strategy: formattedStrategy,
          count: assetCount,
          wallet: wallets[0]
        });
        setIsDiscovering(false);
        onClose();
      } catch (error) {
        console.error('Discovery error');
        setIsDiscovering(false);
      }
    };
  
    return (
      <div 
        className={styles.modalOverlay}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div className="flex items-center gap-3">
              <span className={styles.premiumBadge}>PRO</span>
              <h3 className={styles.modalTitle}>{strategy.label} Strategy Details</h3>
            </div>
            <button 
              onClick={onClose}
              className={styles.closeButton}
            >
              <X size={20} />
            </button>
          </div>
  
          <div className={styles.modalBody}>
            <div className={styles.strategyOverview}>
              <h4>Strategy Overview</h4>
              <p>{strategy.description}</p>
            </div>
  
            <div className={styles.metricsGrid}>
              <div className={styles.metricCard}>
                <h5>Number of Assets</h5>
                <p className={styles.helpText} style={{ 
                  fontSize: '0.8em', 
                  opacity: 0.7, 
                  marginBottom: '8px',
                  color: 'rgba(115, 246, 255, 0.7)'
                }}>
                  Select how many assets you want to discover
                </p>
                <div style={selectStyles.wrapper}>
                  <select
                    value={assetCount}
                    onChange={(e) => setAssetCount(Number(e.target.value))}
                    className={styles.assetCountSelect}
                    style={selectStyles.select}
                  >
                    {assetCountOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown 
                    size={16}
                    style={selectStyles.arrow}
                  />
                </div>
              </div>
              <div className={styles.metricCard}>
                <h5>Risk Level</h5>
                <p style={{ color: getStrategyColor(strategy.id) }}>
                  {strategy.id === 'secure' ? 'Low' :
                   strategy.id === 'diversify' ? 'Medium' :
                   strategy.id === 'juice' ? 'High' : 'Very High'}
                </p>
              </div>
            </div>
  
            <div className={styles.featuresList}>
              {strategy.features?.map((feature, index) => (
                <div key={index} className={styles.featureItem}>
                  <div 
                    className={styles.featureDot} 
                    style={{ backgroundColor: getStrategyColor(strategy.id) }} 
                  />
                  <p>{feature}</p>
                </div>
              ))}
            </div>
  
            <p className={styles.helpText} style={{ 
              fontSize: '0.8em',
              opacity: 0.7,
              marginTop: '16px',
              color: 'rgba(115, 246, 255, 0.7)',
              textAlign: 'center'
            }}>
              Estimated search time: ~15 seconds
            </p>
          </div>
  
          <div className={styles.modalFooter}>
            <button
              onClick={onClose}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <div className={styles.actionButtons} style={{ display: 'flex', gap: '8px' }}>
              <button
                className={styles.applyButton}
                style={{ 
                  background: `linear-gradient(45deg, ${getStrategyColor(strategy.id)}40, transparent)`,
                  borderColor: `${getStrategyColor(strategy.id)}40`,
                  padding: '8px 16px'
                }}
                onClick={handleStartDiscovery}
              >
                Discover {assetCount} Assets
              </button>
            </div>
          </div>
        </div>
  
        {isDiscovering && (
          <LoadingSequence
            strategy={strategy}
            assetCount={assetCount}
            onComplete={handleDiscoveryComplete}
            searchPromise={searchPromise}
          />
        )}
      </div>
    );
  };
  
  const LoadingSequence = ({ strategy, assetCount, onComplete, searchPromise }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const [searchResult, setSearchResult] = useState(null);
    const [searchError, setSearchError] = useState(null);
    const [animationComplete, setAnimationComplete] = useState(false);
  
    const steps = [
      {
        title: 'Initializing Asset Discovery',
        subtext: 'Preparing search parameters...',
        icon: HexagonIcon,
        duration: 4000
      },
      {
        title: 'Asset Analysis',
        subtext: 'Identifying asset characteristics and return profiles...',
        icon: ChartBar,
        duration: 4000
      },
      {
        title: 'Security Analysis',
        subtext: 'Identifying top scoring security assets...',
        icon: ChartBar,
        duration: 4000
      },
      {
        title: 'Finalizing Selection',
        subtext: `Optimizing portfolio of ${assetCount} assets...`,
        icon: Lock,
        duration: 2000
      }
    ];
  
    useEffect(() => {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 1;
        });
      }, 100);
  
      return () => clearInterval(interval);
    }, []);
  
    useEffect(() => {
      if (currentStep < steps.length) {
        const timer = setTimeout(() => {
          setCurrentStep(prev => prev + 1);
        }, steps[currentStep].duration);
  
        return () => clearTimeout(timer);
      } else {
        setAnimationComplete(true);
      }
    }, [currentStep, steps.length]);
  
    // Track the search promise
    useEffect(() => {
      if (!searchPromise) return;
  
      let isMounted = true;
  
      // Add a handler for when the search completes
      searchPromise
        .then(result => {
          if (isMounted) {
            setSearchResult(result);
          }
        })
        .catch(error => {
          if (isMounted) {
            console.error("Search failed");
            setSearchError(error);
          }
        });
  
      return () => {
        isMounted = false;
      };
    }, [searchPromise]);
  
    // Complete the process when both animation and search are done
    useEffect(() => {
      if (animationComplete && (searchResult || searchError)) {
        if (searchError) {
          onComplete(null);
        } else {
          onComplete(searchResult);
        }
      }
    }, [animationComplete, searchResult, searchError, onComplete]);
  
    const getCurrentColor = () => {
      switch (strategy.id) {
        case 'diversify': return '#73F6FF';
        case 'juice': return '#47ff9c';
        case 'secure': return '#9c27b0';
        case 'risk': return '#ffd700';
        default: return '#73F6FF';
      }
    };
  
    const color = getCurrentColor();
  
    return (
      <div className={styles.loadingOverlay}>
        <div className={styles.loadingContainer}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progress}
              style={{ 
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${color}40, ${color})`
              }}
            />
          </div>
  
          <div className={styles.stepsGrid}>
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isComplete = index < currentStep;
              const isFuture = index > currentStep;
  
              return (
                <div 
                  key={index}
                  className={`${styles.stepCard} ${
                    isActive ? styles.active : ''
                  } ${isComplete ? styles.completed : ''}`}
                  style={{ 
                    color,
                    borderColor: isActive ? `${color}80` : `${color}20`,
                    opacity: isFuture ? 0.5 : 1
                  }}
                >
                  <div className={styles.stepIcon}>
                    {isComplete ? (
                      <div className={styles.checkIcon}>
                        <Check size={16} color="#000" />
                      </div>
                    ) : (
                      <Icon 
                        size={24}
                        style={{ 
                          color: isActive ? color : isFuture ? `${color}40` : color
                        }}
                      />
                    )}
                  </div>
                  <div 
                    className={styles.stepTitle}
                    style={{ 
                      color: isActive ? color : isFuture ? `${color}40` : color
                    }}
                  >
                    {step.title}
                  </div>
                  <div 
                    className={styles.stepSubtext}
                    style={{ 
                      color: isActive ? color : isFuture ? `${color}40` : color
                    }}
                  >
                    {step.subtext}
                  </div>
                  {isActive && (
                    <div 
                      className={styles.stepGlow}
                      style={{ background: color }}
                    />
                  )}
                </div>
              );
            })}
          </div>
  
          <div 
            className={styles.statusText}
            style={{ color }}
          >
            {currentStep >= steps.length 
              ? searchResult 
                ? 'Discovery Complete!' 
                : 'Finalizing results...'
              : `${Math.round(progress)}% Complete`}
          </div>
        </div>
      </div>
    );
  };


  // Non-session storage cache implementation
  // This relies on backend redis caching
  // of 30 minutes
  useEffect(() => {
    const loadTokenBankData = async () => {
      setIsLoading(true);
      try {
        if (!auth.user?.access_token) {
          setError('Not authenticated');
          return;
        }
        const data = await getTokenBankData(auth.user.access_token);
        setTokenBankData(data);
      } catch (error) {
        setError('Failed to fetch token bank data');
      } finally {
        setIsLoading(false);
      }
    };
    loadTokenBankData();
  }, [auth.user?.access_token]);


  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTokenSelect = async (token, chain) => {
    if (!auth.user?.access_token) {
      toast.error('User not authenticated');
      return;
    }
    
    // Reset all scores when selecting a new token
    setSelectedToken(token);
    setHolderScore(0);
    setBuySellScore(0);
    setLiquidityScore(0);
    setMarketActivityScore(0);
    setPairData([]);
    setTokenMetadata(null);
    setHolderCountHistory([]);
    
    try {
      // Parallel API calls for better performance
      const [metadata, pairData] = await Promise.all([
        getTokenMetadata(
          [token.contract_address], 
          [chain], 
          auth.user.access_token
        ),
        getTokenPairData(
          auth.user.access_token,
          token.contract_address, 
          chain, 
          10
        )
      ]);
  
      // Set metadata if available
      if (metadata && metadata.length > 0) {
        setTokenMetadata(metadata[0]);
      }
  
      // Fetch historical holder data
      await fetchHolderHistory(token.contract_address, token.chain);
  
      // Set pair data and extract scores if available
      if (pairData?.data && Array.isArray(pairData.data) && pairData.data.length > 0) {
        setPairData(pairData.data);
        
        // Find the metrics object in the array
        const metricsData = pairData.data.find(item => 
          item.buy_sell_pressure_score !== undefined &&
          item.liquidity_health_score !== undefined &&
          item.market_activity_score !== undefined
        );
  
        if (metricsData) {
          setBuySellScore(metricsData.buy_sell_pressure_score);
          setLiquidityScore(metricsData.liquidity_health_score);
          setMarketActivityScore(metricsData.market_activity_score);
        } else {
          // Metrics not found in the data
          setBuySellScore("NOT_FOUND");
          setLiquidityScore("NOT_FOUND");
          setMarketActivityScore("NOT_FOUND");
        }
      } else {
        // Empty pair data - indicate no data available
        setPairData([]);
        setBuySellScore("NOT_FOUND");
        setLiquidityScore("NOT_FOUND");
        setMarketActivityScore("NOT_FOUND");
      }
  
    } catch (error) {
      console.error('Error in token selection');
      if (error.response) {
        console.error('API Error Response');
      }
      
      // Set error state for metrics
      setBuySellScore("NOT_FOUND");
      setLiquidityScore("NOT_FOUND");
      setMarketActivityScore("NOT_FOUND");
    }
    
    // Set a timeout to ensure we don't show spinners forever
    // const timeoutRef = useRef(null);

    // In handleTokenSelect
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setBuySellScore(prev => prev === null ? "NOT_FOUND" : prev);
        setLiquidityScore(prev => prev === null ? "NOT_FOUND" : prev);
        setMarketActivityScore(prev => prev === null ? "NOT_FOUND" : prev);
        setHolderScore(prev => prev === null ? "NOT_FOUND" : prev);
      }, 5000);
  };

  const CustomYAxisTick = ({ x, y, payload }) => {
    // Format the value to a compact number with 1 decimal place
    const formattedValue = new Intl.NumberFormat('en-US', { 
      notation: 'compact', 
      maximumFractionDigits: 1 
    }).format(payload.value);
    
    return (
      <text 
        x={x} 
        y={y} 
        dy={4} 
        fill="#73F6FF40" 
        fontSize={12}
        textAnchor="end"
      >
        {formattedValue}
      </text>
    );
  };

  const SocialLinks = ({ links }) => {
    if (!links) return null;
    
    return (
      <div className={styles.socialLinks}>
        {links.github && (
          <a href={links.github} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
            <Github size={16} />
          </a>
        )}
        {links.twitter && (
          <a href={links.twitter} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
            <Twitter size={16} />
          </a>
        )}
        {links.discord && (
          <a href={links.discord} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
            <MessageCircle size={16} />
          </a>
        )}
      </div>
    );
  };

  const debouncedSearchValue = useDebounce(searchValue, 500);
  const filteredTokens = useMemo(() => {
    return applyMultipleFilters(tokenBankData, [debouncedSearchValue, ...activeFilters]); 
  }, [tokenBankData, debouncedSearchValue, activeFilters]);


  // Helper function to get top assets based on multiple criteria
  const getTopAssets = (tokens, securityScores) => {
    if (!tokens || tokens.length === 0) return [];
    
    // Calculate combined ranking score for each token
    const scoredTokens = tokens.map(token => {
      // Normalize values to 0-100 scale
      const securityScore = securityScores[token.contract_address] || 0;
      
      // Weights for different factors
      const weights = {
        security: 0.4,
        marketCap: 0.3,
        weeklyPerformance: 0.3
      };
      
      // Get market cap rank (higher market cap = higher rank)
      const marketCapRank = token.market_cap_usd ? 
        token.market_cap_usd / Math.max(...tokens.map(t => t.market_cap_usd || 0)) * 100 : 0;
        
      // Get 7d performance score (higher = better)
      const sevenDayChange = token.price_7d_percent_change || 0;
      // Normalize to 0-100 scale - find max positive change or zero if all negative
      const maxPositiveChange = Math.max(...tokens.map(t => t.price_7d_percent_change || 0), 0);
      // Use different calculation depending on positive or negative change
      const weeklyPerformanceRank = sevenDayChange > 0 ?
        (sevenDayChange / maxPositiveChange) * 100 : 0;
      
      // Calculate combined score
      const combinedScore = 
        (securityScore * weights.security) + 
        (marketCapRank * weights.marketCap) + 
        (weeklyPerformanceRank * weights.weeklyPerformance);
        
      return { ...token, combinedScore };
    });
    
    // Sort by combined score and take top 3
    return scoredTokens
      .sort((a, b) => b.combinedScore - a.combinedScore)
      .slice(0, 3);
  };
  
  useEffect(() => {
    const fetchTopAssetsMetadata = async () => {
      if (!activeStrategyBanner || !filteredTokens.length || !auth.user?.access_token) return;
      
      try {
        // Get initial top assets
        const topAssets = getTopAssets(filteredTokens, securityScores);
        const addresses = topAssets.map(token => token.contract_address);
        const chains = topAssets.map(token => token.chain || 'eth');
        
        // Fetch metadata for these assets
        const metadata = await getTokenMetadata(addresses, chains, auth.user.access_token);
        
        // Convert to lookup object
        const metadataMap = metadata.reduce((acc, item) => {
          acc[item.contract_address] = item;
          return acc;
        }, {});
        
        setTopAssetsMetadata(metadataMap);
      } catch (error) {
        console.error('Error fetching metadata for top assets');
      }
    };
    
    fetchTopAssetsMetadata();
  }, [activeStrategyBanner, filteredTokens, securityScores, auth.user?.access_token]);



  useEffect(() => {
  setCurrentPage(1);
  }, [debouncedSearchValue, activeFilters]);

  const totalPages = Math.ceil(filteredTokens.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentTokens = filteredTokens.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedToken(null);
  };

  useEffect(() => {
    const fetchSecurityScores = async () => {
      if (!auth.user?.access_token) return;
   
      const filteredSlice = filteredTokens.slice(startIndex, endIndex);
      const addresses = filteredSlice.map(token => token.contract_address);
      const chains = filteredSlice.map(token => token.chain || 'eth');
      
      try {
        const scores = await getSecurityScores(addresses, chains, auth.user.access_token);
        setSecurityScores(prev => ({...prev, ...scores}));
      } catch (error) {
        console.error('Error fetching security scores');
      }
    };
   
    if (filteredTokens.length) {
      fetchSecurityScores();
    }
   }, [filteredTokens, currentPage, auth.user?.access_token]);

  const formatPrice = (price) => {
    if (price < 0.00001) return price.toExponential(4);
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    });
  };

  // useeffect for help search 
  useEffect(() => {
    if (searchValue.trim().toLowerCase() === "help") {
      setIsSearchGuideOpen(true);
      setSearchValue(''); // Clear the input after showing help
    }
  }, [searchValue]);

  const formatMarketCap = (marketCap) => {
    return (Number(marketCap) / 1000000).toFixed(2) + 'M';
  };

  const formatPercentage = (value) => {
    const num = Number(value);
    const formatted = num.toFixed(2);
    return `${num > 0 ? '+' : ''}${formatted}%`;
  };

  const formatLongNumber = (num) => {
    if (!num) return 'N/A';
    const value = Number(num);
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
    return value.toFixed(2);
  };
  
  const SecurityScore = ({ score }) => {
    const getScoreColor = (value) => {
      if (value < 40) return '#f44336';
      if (value < 70) return '#9c27b0';
      return '#73F6FF';
    };
    
    const colorStyles = {
      progress: {
        background: getScoreColor(score),
        boxShadow: `0 0 10px ${getScoreColor(score)}40`
      }
    };
  
    return (
      <div className={styles.securityScoreCell}>
        <div className={styles.securityScoreContent}>
          <div className={styles.scoreSegments}>
            {[0,1,2,3,4].map((segment) => {
              const normalizedScore = score / 20;
              const isFullyFilled = segment < Math.floor(normalizedScore);
              const isPartiallyFilled = segment === Math.floor(normalizedScore);
              const partialFillPercentage = isPartiallyFilled ? (normalizedScore % 1) * 100 : 0;
              
              let segmentStyle = {};
              if (isFullyFilled) {
                segmentStyle = colorStyles.progress;
              } else if (isPartiallyFilled && partialFillPercentage > 0) {
                const color = getScoreColor(score);
                segmentStyle = {
                  background: `linear-gradient(to right, ${color} ${partialFillPercentage}%, rgba(115, 246, 255, 0.1) ${partialFillPercentage}%)`,
                  boxShadow: colorStyles.progress.boxShadow
                };
              }
              
              return (
                <div 
                  key={segment}
                  className={`${styles.segment} ${isFullyFilled ? styles.segmentFilled : ''}`}
                  style={segmentStyle}
                />
              );
            })}
          </div>
          <span 
            className={styles.securityScoreValue}
            style={{ color: getScoreColor(score) }}
          >
            {score?.toFixed(1) || '0.0'}
          </span>
        </div>
      </div>
    );
  };

  if (isLoading) return <Spinner />;
  if (error) return <div className={styles.error}>{error}</div>;

  const sortedHolderHistory = [...holderCountHistory].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <div className={styles.content}>
    <div className={styles.tableContainer} style={{ 
      maxHeight: isTableCollapsed ? '80px' : undefined, 
      overflow: 'hidden', 
      transition: 'max-height 0.3s ease-in-out' 
    }}>

    <div className={styles.searchContainer} ref={dropdownRef}>
      <div className={styles.searchBar}>
        <div className={styles.commandButton}>
          <Command size={16} className={styles.commandIcon} />
          {/* <span className={styles.commandText}>⌘</span> */}
          <span className={styles.commandText}>K</span>
        </div>
        
        <div className={styles.searchContent}>
        <input
        ref={searchInputRef}
        type="text"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onFocus={() => setIsSearchFocused(true)} 
        className={styles.searchInput}
        placeholder={activeFilters.length ? "Add another filter..." : "Try 'top performers' or 'help' for more options"}
        />
        <div className={styles.activeFilters}>
          {activeFilters.map((filter, i) => (
            <div key={i} className={styles.filterTag}>
              <span>{filter}</span>
              <button onClick={() => removeFilter(filter)} className={styles.removeFilter}>×</button>
            </div>
          ))}
        </div>
      </div>

        <button 
          className={styles.filterButton} 
          onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
        >
          <span className={styles.filterText}>Pro Filters</span>
          <ChevronDown size={16} className={styles.filterIcon} />
        </button>
      </div>
      
      {isFilterDropdownOpen && (
        <div className={styles.filterPanel}>
          <div className={styles.filterColumns}>
            <div className={styles.filterSection}>
              <ConstructionBadge>
              <h3>Personalized Portfolio Strategies</h3>
              </ConstructionBadge>
              <div className={styles.strategyList}>
                <button
                  type="button"
                  className={`${styles.strategyItem} ${
                    activeStrategy === 'diversify' ? styles.selected : ''
                  }`}
                  onClick={() => {
                    const strategy = {
                      id: 'diversify',
                      label: 'Portfolio Diversification',
                      description: 'Discover assets that could diversify your portfolio...',
                      features: [
                        'Cross-sector allocation optimization',
                        'Risk-weighted position sizing',
                        'Correlation analysis between assets',
                      ]
                    };
                    handleStrategySelect(strategy);
                  }}
                >
                  <div className={styles.content}>
                    <div className={styles.title}>Diversify Portfolio</div>
                    <div className={styles.description}>Balanced risk across sectors</div>
                    <Sparkles size={18} className={styles.strategyIcon} />
                  </div>
                  <div className={styles.buttonShine} />
                  <div className={styles.buttonGlow} />
                  <span className={styles.proTag}>PRO</span>
                </button>

                <button
                  type="button"
                  disabled // disable clicking until finished
                  className={`${styles.strategyItem} ${
                    activeStrategy === 'juice' ? styles.selected : ''
                  }`}
                  onClick={() => {
                    const strategy = {
                      id: 'juice',
                      label: 'Portfolio Diversification',
                      description: 'Optimize your portfolio across different sectors...',
                      features: [
                        'Cross-sector allocation optimization',
                        'Risk-weighted position sizing',
                        'Correlation analysis between assets',
                        'Regular rebalancing recommendations'
                      ]
                    };
                    handleStrategySelect(strategy);
                  }}
                >
                  <div className={styles.content}>
                    <div className={styles.title}>Growth Strategy</div>
                    <div className={styles.description}>High growth potential</div>
                    <TrendingUp size={18} className={styles.strategyIcon} />
                  </div>
                  <div className={styles.buttonShine} />
                  <div className={styles.buttonGlow} />
                  <span className={styles.proTag}>PRO</span>
                </button>

                <button
                  type="button"
                  disabled // disable clicking until finished
                  className={`${styles.strategyItem} ${
                    activeStrategy === 'risk' ? styles.selected : ''
                  }`}
                  onClick={() => {
                    const strategy = {
                      id: 'risk',
                      label: 'Maximum Return',
                      description: 'Target high return opportunities...',
                      features: [
                        'Cross-sector allocation optimization',
                        'Risk-weighted position sizing',
                        'Correlation analysis between assets',
                        'Regular rebalancing recommendations'
                      ]
                    };
                    handleStrategySelect(strategy);
                  }}
                >
                  <div className={styles.content}>
                    <div className={styles.title}>Maximum Yield</div>
                    <div className={styles.description}>Optimal yield focus</div>
                    <Search size={18} className={styles.strategyIcon} />
                  </div>
                  <div className={styles.buttonShine} />
                  <div className={styles.buttonGlow} />
                  <span className={styles.proTag}>PRO</span>
                </button>

                <button
                  type="button"
                  disabled // disable clicking until finished
                  className={`${styles.strategyItem} ${
                    activeStrategy === 'secure' ? styles.selected : ''
                  }`}
                  onClick={() => {
                    const strategy = {
                      id: 'secure',
                      label: 'Secure',
                      description: 'Target high return opportunities...',
                      features: [
                        'Cross-sector allocation optimization',
                        'Risk-weighted position sizing',
                        'Correlation analysis between assets',
                        'Regular rebalancing recommendations'
                      ]
                    };
                    handleStrategySelect(strategy);
                  }}
                >
                  <div className={styles.content}>
                    <div className={styles.title}>Juice + DeFi</div>
                    <div className={styles.description}>Experimental approach combining DeFi opportunities</div>
                    <SlidersHorizontal size={18} className={styles.strategyIcon} />
                  </div>
                  <div className={styles.buttonShine} />
                  <div className={styles.buttonGlow} />
                  <span className={styles.proTag}>PRO</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && selectedStrategy && (
          <StrategyModal 
            strategy={selectedStrategy}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </div>

      
      {activeStrategyBanner && (
        <div className={styles.strategyCard}>
          <div className={styles.cardHeader}>
            <div className={styles.headerTop}>
              <div className={styles.badgeRow}>
                <span className={styles.strategyBadge}>Strategy Selected: {activeStrategyBanner.strategy}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className={styles.walletBadge}>
                  <span className={styles.walletLabel}>Wallet:</span>
                  <span className={styles.walletAddress}>{activeStrategyBanner.wallet?.slice(0, 6)}...{activeStrategyBanner.wallet?.slice(-4)}</span>
                </div>
                <button 
                  className={styles.resetButton}
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
            
            <div>
              <div className={styles.strategyDescription}>
                {/* These assets have been algorithmically selected for {activeStrategyBanner.strategy.toLowerCase()} characteristics based on market behavior and on-chain data. */}
                <span className={styles.disclaimerTooltip} title="This selection is for informational purposes only and does not constitute investment advice.">ⓘ</span>
              </div>
            </div>
          </div>
          
          <div className={styles.assetsContainer}>
            {(() => {
              // Get initial top assets
              const topAssets = getTopAssets(filteredTokens, securityScores);
              
              // Filter out stablecoins if we have metadata
              const displayAssets = topAssets.filter(token => {
                const metadata = topAssetsMetadata[token.contract_address];
                if (!metadata || !metadata.categories) return true;
                
                return !metadata.categories.some(category => 
                  category.toLowerCase().includes('stablecoins')
                );
              });
              
              // If filtering removed too many, get more assets
              if (displayAssets.length < 3 && filteredTokens.length > 3) {
                // Get addresses we've already processed
                const usedAddresses = new Set(topAssets.map(t => t.contract_address));
                
                // Get more assets excluding ones we've already seen
                const additionalAssets = getTopAssets(
                  filteredTokens.filter(t => !usedAddresses.has(t.contract_address)),
                  securityScores
                );
                
                // Add more assets until we reach 3 or run out
                for (const asset of additionalAssets) {
                  const metadata = topAssetsMetadata[asset.contract_address];
                  const isStablecoin = metadata?.categories?.some(category => 
                    category.toLowerCase().includes('stablecoins')
                  );
                  
                  if (!isStablecoin && displayAssets.length < 3) {
                    displayAssets.push(asset);
                  }
                }
              }
              
              return displayAssets.map((token, index) => {
                return (
                  // <div key={token.token_symbol} className={styles.assetTile} onClick={() => handleTokenSelect(token, "eth")}>
                  <div key={token.token_symbol} className={styles.assetTile} onClick={() => handleTokenSelect(token, token.chain)}>
                    <div className={styles.assetHeader}>
                      <div className={styles.assetIconWrapper}>
                        {token.token_logo ? (
                          <img src={token.token_logo} alt={token.token_symbol} className={styles.assetIcon} />
                        ) : (
                          <div className={styles.assetFallback}>{token.token_symbol[0]}</div>
                        )}
                      </div>
                      <div className={styles.assetMeta}>
                        <span className={styles.assetSymbol}>{token.token_symbol}</span>
                      </div>
                    </div>
                    
                    <div className={styles.priceRow}>
                      <span className={styles.priceValue}>${formatPrice(Number(token.price_usd))}</span>
                      <span className={`${styles.priceChange} ${Number(token.price_24h_percent_change) >= 0 ? styles.positive : styles.negative}`}>
                        {Number(token.price_24h_percent_change) >= 0 ? '↑' : '↓'}{formatPercentage(token.price_24h_percent_change)}
                      </span>
                    </div>
                    
                    <div className={styles.actionButtons}>
                      <button 
                        className={`${styles.actionButton} ${styles.viewButton}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // handleTokenSelect(token, "eth");
                          handleTokenSelect(token, token.chain);
                        }}
                      >
                        View Details
                      </button>
                      <button 
                        className={`${styles.actionButton} ${styles.rationaleButton}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRationaleToken(token);
                          setRationaleModalOpen(true);
                        }}
                      >
                        Rationale
                      </button>
                      {/* <button 
                        className={`${styles.actionButton} ${styles.rationaleButton}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Show rationale logic here
                          toast.info(`${token.token_symbol} was selected based on security score and market performance`);
                        }}
                      >
                        Rationale
                      </button> */}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

        <table className={styles.table}>
          <thead>
            <tr>
              <th>Token</th>
              <th>Price</th>
              <th>24h</th>
              <th>7d</th>
              <th>Market Cap</th>
              {/* <th>Type</th> */}
              <th>Security Score</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {currentTokens.map((token, index) => (
              <tr
                key={index}
                className={`${styles.tokenRow} ${selectedToken === token ? styles.selected : ''}`}
                // onClick={() => handleTokenSelect(token, "eth")}
                onClick={() => handleTokenSelect(token, token.chain)}
              >
                <td>
                  <div className={styles.tokenCell}>
                    <div className={styles.tokenIcon}>
                      {token.token_logo ? (
                        <>
                          <img 
                            src={token.token_logo} 
                            alt={token.token_symbol}
                            className={styles.tokenLogo}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                          <div className={styles.tokenFallback} style={{ display: 'none' }}>
                            {token.token_symbol[0]}
                          </div>
                        </>
                      ) : (
                        <div className={styles.tokenFallback}>
                          {token.token_symbol[0]}
                        </div>
                      )}
                    </div>
                    <div className={styles.tokenInfo}>
                      <div className={styles.tokenNameRow}>
                        <div className={styles.tokenName}>{token.token_symbol}</div>
                        {/* <span className={styles.chainPill}>{token.chain?.toUpperCase() || 'ETH'}</span> */}
                        <span className={`${styles.chainPill} ${styles[token.chain]}`}>
                          {token.chain?.toUpperCase()}
                        </span>
                      </div>
                      <div className={styles.tokenAddress}>{token.contract_address}</div>
                    </div>
                    {/* <div className={styles.tokenInfo}>
                      <div className={styles.tokenName}>{token.token_symbol}</div>
                      <div className={styles.tokenAddress}>{token.contract_address}</div>
                    </div> */}
                  </div>
                </td>
                <td>${formatPrice(Number(token.price_usd))}</td>
                <td style={{ color: Number(token.price_24h_percent_change) >= 0 ? '#47ff9c' : '#ff4747' }}>
                  {formatPercentage(token.price_24h_percent_change)}
                </td>
                <td style={{ color: Number(token.price_7d_percent_change) >= 0 ? '#47ff9c' : '#ff4747' }}>
                  {formatPercentage(token.price_7d_percent_change)}
                </td>
                <td>${formatMarketCap(token.market_cap_usd)}</td>
                <td>
                  <SecurityScore score={securityScores[token.contract_address] || 0} />
                </td>
                <td>
                  <button id="discovery-token-expand-link" className={styles.viewButton}>
                    <ExternalLink size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={styles.pagination}>
          <button 
            className={styles.paginationButton}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={14} />
          </button>
          <span className={styles.pageInfo}>
            {currentPage} / {totalPages}
          </span>
          <button 
            className={styles.paginationButton}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {selectedToken && (
        <div className={styles.analysisPanel}>
          <div className={styles.tabNavigation}>
            <button 
              className={`${styles.tabButton} ${activeTab === 'info' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('info')}
            >
              Token Info
            </button>
            {/* <button 
              className={`${styles.tabButton} ${activeTab === 'price' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('price')}
            >
              Price Chart
            </button> */}
            <button 
              className={`${styles.tabButton} ${activeTab === 'holder_detailed' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('holder_detailed')}
            >
              Holder Analytics <ProBadgeSmall />
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'rug_check' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('rug_check')}
            >
              Token Health <ProBadgeSmall />
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'impact' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('impact')}
            >
              Portfolio Impact <ProPlusBadgeSmall />
            </button>

            <button 
              className={`${styles.tabButton} ${activeTab === 'monitor' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('monitor')}
            >
              Live Monitor <ProPlusBadgeSmall />
            </button>

            {/* Vertical divider */}
            <div className={styles.divider}></div>

            {/* Token info - replicating your existing structure */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingRight: '16px' }}>
              {tokenMetadata?.logo && (
                <img 
                  src={tokenMetadata.logo} 
                  alt={selectedToken.token_symbol}
                  className={styles.tokenLogoSmall}
                />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className={styles.tokenName}>{selectedToken.token_name}</div>
                <div className={styles.tokenSymbol}>${selectedToken.token_symbol}</div>
                <span className={`${styles.chainPill} ${styles[selectedToken.chain]}`}>
                  {selectedToken.chain?.toUpperCase()}
              </span>
              </div>
            </div>
          </div>

          <div className={styles.tabContent}>
            {activeTab === 'info' && (
              <div className={styles.healthContainer}>
                {/* Enhanced Token Identity Header */}
                <div style={{
                  padding: '16px',
                  borderBottom: '1px solid rgba(115, 246, 255, 0.1)',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: '24px',
                  alignItems: 'start'
                }}>
                  {/* Token Basic Info */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {tokenMetadata?.logo && (
                      <img 
                        src={tokenMetadata.logo} 
                        alt={selectedToken.token_symbol}
                        className={styles.tokenLogoSmall}
                      />
                    )}
                    <div>
                      <div className={styles.tokenName}>{selectedToken.token_name}</div>
                      <div className={styles.tokenSymbol}>${selectedToken.token_symbol}</div>
                    </div>
                  </div>

                  {/* Description and Categories */}
                  <div style={{ minWidth: 0 }}>
                    <div className={styles.description} style={{ 
                      fontSize: '0.9em',
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: tokenMetadata?.categories?.length ? '12px' : '0'  
                    }}>
                      {tokenMetadata?.description || 
                        <span style={{ color: 'rgba(115, 246, 255, 0.4)' }}>
                          No description available for this token.
                        </span>
                      }
                    </div>
                    {tokenMetadata?.categories && tokenMetadata.categories.length > 0 && (
                      <div className={styles.categoryTags}>
                        {tokenMetadata.categories.map((category, index) => (
                          <span key={index} className={styles.categoryTag}>
                            {category}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Social Links */}
                  <div className={styles.socialLinks}>
                    <SocialLinks links={tokenMetadata?.links} />
                  </div>
                </div>

                {/* Metrics Container */}
                <div style={{
                  background: 'rgba(23, 23, 30, 0.95)',
                  margin: '16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(115, 246, 255, 0.1)'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    borderBottom: '1px solid rgba(115, 246, 255, 0.1)',
                    padding: '12px'
                  }}>
                    <div className={styles.tokenMetaItem}>
                      <span className={styles.metaLabel}>Price</span>
                      <div className={styles.metricValue}>
                        ${formatPrice(Number(selectedToken.price_usd))}
                        <span className={`${Number(selectedToken.price_24h_percent_change) >= 0 ? styles.positive : styles.negative}`} 
                              style={{ marginLeft: '8px', fontSize: '0.85em' }}>
                          {formatPercentage(selectedToken.price_24h_percent_change)}
                        </span>
                      </div>
                    </div>
                    <div className={styles.tokenMetaItem}>
                      <span className={styles.metaLabel}>Market Cap</span>
                      <div className={styles.metricValue}>${formatLongNumber(selectedToken.market_cap_usd)}</div>
                    </div>
                    <div className={styles.tokenMetaItem}>
                      <span className={styles.metaLabel}>FDV</span>
                      <div className={styles.metricValue}>${formatLongNumber(tokenMetadata?.fully_diluted_valuation)}</div>
                    </div>
                    <div className={styles.tokenMetaItem}>
                      <span className={styles.metaLabel}>Total Supply</span>
                      <div className={styles.metricValue}>{formatLongNumber(tokenMetadata?.total_supply_formatted)}</div>
                    </div>
                    <div className={styles.tokenMetaItem}>
                      <span className={styles.metaLabel}>Days Since Creation</span>
                      <div className={styles.metricValue}>
                        {tokenMetadata?.created_at ? Math.floor((new Date() - new Date(tokenMetadata.created_at)) / (1000 * 60 * 60 * 24)) : 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Health Metrics Row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    padding: '12px',
                    gap: '12px'
                  }}>
                    <div className={styles.tokenMetaItem}>
                      {holderScore === null ? (
                        <div style={{ 
                          minHeight: '80px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center' 
                        }}>
                          <Spinner />
                        </div>
                      ) : (
                        renderMetric(
                          "Holder Growth", 
                          getRiskLevel(holderScore), 
                          holderScore,
                          "Measures holder acquisition and retention on a 0-5 scale. 60% based on overall growth rate and 40% on holder retention patterns. Higher scores indicate strong, sustainable community growth."
                        )
                      )}
                    </div>
                    <div className={styles.tokenMetaItem}>
                      {buySellScore === null ? (
                        <div style={{ 
                          minHeight: '80px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center' 
                        }}>
                          <Spinner />
                        </div>
                      ) : (
                        renderMetric("Buy/Sell Pressure", getRiskLevel(buySellScore), buySellScore)
                      )}
                    </div>
                    <div className={styles.tokenMetaItem}>
                      {liquidityScore === null ? (
                        <div style={{ 
                          minHeight: '80px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center' 
                        }}>
                          <Spinner />
                        </div>
                      ) : (
                        renderMetric("Liquidity Health", getRiskLevel(liquidityScore), liquidityScore)
                      )}
                    </div>
                    <div className={styles.tokenMetaItem}>
                      {marketActivityScore === null ? (
                        <div style={{ 
                          minHeight: '80px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center' 
                        }}>
                          <Spinner />
                        </div>
                      ) : (
                        renderMetric("Market Activity", getRiskLevel(marketActivityScore), marketActivityScore)
                      )}
                    </div>
                  </div>
                </div>


                <div style={{
                  margin: '16px',
                  background: 'rgba(23, 23, 30, 0.95)',
                  borderRadius: '8px',
                  border: '1px solid rgba(115, 246, 255, 0.1)',
                  padding: '16px',
                  display: 'grid',
                  gridTemplateColumns: '3fr 1px 2fr',
                  gap: '24px',
                  alignItems: 'center'
                }}>
                  <div>
                    <div className="chartHeader">
                      <h3 className={`chartTitle`}>Holder Count - {selectedToken.token_symbol}</h3>
                    </div>
                    {holderCountHistory.length === 0 ? (
                      <div style={{ 
                        height: 300, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}>
                        <Spinner />
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart 
                          data={sortedHolderHistory}
                          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <pattern id="holderChartBackgroundImage" patternUnits="userSpaceOnUse" width="100%" height="100%">
                              <image 
                                href={allAssetsImage}
                                x="30%"
                                y="20%"
                                width="40%"
                                height="40%"
                                opacity="0.05"
                                preserveAspectRatio="xMidYMid meet"
                                transform="translate(-30%, -30%)"
                              />
                            </pattern>
                          </defs>
                          
                          {/* Background rectangle with image */}
                          <rect 
                            x="0" 
                            y="0" 
                            width="100%" 
                            height="100%" 
                            fill="url(#holderChartBackgroundImage)" 
                          />
                          
                          <XAxis 
                            dataKey="date" 
                            stroke="#73F6FF40"
                            fontSize={12}
                          />
                          <YAxis 
                            stroke="#73F6FF40"
                            tick={<CustomYAxisTick />}
                            domain={['auto', 'auto']}
                            scale="linear"
                            tickFormatter={(value) => new Intl.NumberFormat('en-US', { 
                              notation: 'compact',
                              maximumFractionDigits: 1
                            }).format(value)}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Line 
                            type="monotone" 
                            dataKey="holders" 
                            stroke="#73F6FF" 
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4, stroke: '#73F6FF', strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  <div style={{ width: '1px', height: '100%', background: 'rgba(115, 246, 255, 0.1)' }} />

                  {pairData.length === 0 ? (
                    <div style={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      {buySellScore === 0 ? (
                        <Spinner />
                      ) : (
                        <div style={{
                          color: 'rgba(115, 246, 255, 0.5)',
                          fontSize: '0.9em',
                          padding: '24px',
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <div>No trading pair data available</div>
                          <div style={{ fontSize: '0.8em', opacity: 0.7 }}>
                            This token may not have enough liquidity
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <CompactTokenPairMetrics 
                      pairData={pairData}
                      // selectedChain="eth"  
                      selectedChain={selectedToken.chain}  
                    />
                  )}
                </div>
              </div>
            )}

            {/* {activeTab === 'price' && (
              <div className={styles.priceChartContainer}>
                <div className={styles.priceChartContent} style={{ height: '600px' }}>
                  <div className={styles.priceChart} style={{ height: 'calc(100% - 40px)' }}>
                    <OHLCVChart tokenAddress={selectedToken.contract_address} />
                  </div>
                </div>
              </div>
            )} */}

            {/* {activeTab === 'sniper' && (
              <div className={styles.sniperContainer}>
                <div className={styles.sniperContent}>
                  <div className={styles.analysisContent}>
                    <ProSection membershipStatus={stripeConfirmation} onUpgrade={() => window.open('/billing', '_blank')}>
                      <TokenSniper 
                        accessToken={auth.user?.access_token}
                        contractAddress="0x85218527945d48167A682e277867F1539AD00068"
                        chain="eth"
                        blocksAfterCreation={3}
                      />
                    </ProSection>
                  </div>
                </div>
              </div>
            )} */}

            {/* {activeTab === 'sniper' && (
              <div className={styles.sniperContainer}>
                <div className={styles.sniperContent}>
                  <div className={styles.analysisContent}>
                    <ConstructionSection isAvailable={false}>
                      <TokenSniper 
                        accessToken={auth.user?.access_token}
                        contractAddress="0x85218527945d48167A682e277867F1539AD00068"
                        // chain="eth"
                        chain={selectedToken?.chain || "eth"}
                        blocksAfterCreation={3}
                      />
                    </ConstructionSection>
                  </div>
                </div>
              </div>
            )} */}


            {/* {activeTab === 'holder_detailed' && (
              <div className={styles.sniperContainer}>
                <div className={styles.sniperContent}>
                  <div className={styles.analysisContent}>
                    <ProSection membershipStatus={stripeConfirmation} onUpgrade={() => window.open('/billing', '_blank')}>
                      <HolderAnalyticsDetailed 
                        accessToken={auth.user?.access_token}
                        contractAddress={selectedToken?.contract_address || "0x85218527945d48167A682e277867F1539AD00068"}
                        chain={selectedToken?.chain || "eth"}
                      />
                    </ProSection>
                  </div>
                </div>
              </div>
            )} */}

            {activeTab === 'holder_detailed' && (
              <div className={styles.sniperContainer}>
                <div className={styles.sniperContent}>
                  <div className={styles.analysisContent}>
                    <ProSection membershipStatus={stripeConfirmation} onUpgrade={() => window.open('/billing', '_blank')}>
                      <HolderAnalyticsDetailed 
                        accessToken={auth.user?.access_token}
                        contractAddress={selectedToken?.contract_address || "0x85218527945d48167A682e277867F1539AD00068"}
                        chain={selectedToken?.chain || "eth"}
                      />
                    </ProSection>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'rug_check' && (
              <div className={styles.sniperContainer}>
                <div className={styles.sniperContent}>
                  <div className={styles.analysisContent}>
                    <ConstructionSection isAvailable={false}>
                      <RugCheckComponent />
                    </ConstructionSection>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'impact' && (
              <div className={styles.sniperContainer}>
                <div className={styles.sniperContent}>
                  <div className={styles.analysisContent}>
                    <ConstructionSection isAvailable={false}>
                      <PortfolioImpactComponent />
                    </ConstructionSection>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'monitor' && (
              <div className={styles.sniperContainer}>
                <div className={styles.sniperContent}>
                  <div className={styles.analysisContent}>
                    <ConstructionSection isAvailable={false}>
                      <LiveMonitor />
                    </ConstructionSection>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* <StrategyModal 
        strategy={selectedStrategy} 
        onClose={() => setSelectedStrategy(null)}
      /> */}

      {isSearchGuideOpen && (
        <div className={styles.modalOverlay} onClick={(e) => {
          if (e.target === e.currentTarget) setIsSearchGuideOpen(false);
        }}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Search Guide</h3>
              <button onClick={() => setIsSearchGuideOpen(false)} className={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <SearchGuide />
          </div>
        </div>
      )}
      
      
      {rationaleModalOpen && selectedRationaleToken && (
        <AssetRationaleModal
          token={selectedRationaleToken}
          strategyType={activeStrategyBanner?.strategy}
          onClose={() => setRationaleModalOpen(false)}
        />
      )}
    </div>
  );
};

export default DataFetchingComponent;










